import constate from 'constate'
import { useCallback, useRef, useState } from 'react'
import { BaseBudget, Budget, BudgetWithIds } from 'typings'
import { useBudgetMutation } from './useBudgetMutation'

export enum BudgetActions {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  NONE = 'none',
}

export enum BudgetPermissions {
  READ = 'read',
  WRITE = 'write',
  NONE = 'none',
}

type ProcessBudgetType = ({
  budget,
  promotionId,
  promotionName,
}: {
  budget?: Budget
  promotionId?: string
  promotionName: string
}) => Promise<{
  success: boolean
  budget?: BudgetWithIds
  errorCode?: number
}>

type BudgetContextType = {
  action: React.MutableRefObject<BudgetActions>
  originalValue: React.MutableRefObject<BudgetWithIds | undefined>
  permission: BudgetPermissions
  setOriginalValue: (value: BudgetWithIds) => void
  setPermission: (newPermission: BudgetPermissions) => void
  setAction: (newAction: BudgetActions) => void
}

type BudgetProcessingType = {
  processBudget: ProcessBudgetType
}

type Props = {
  budgetPermission?: BudgetPermissions
}
const useBudget = (props: Props) => {
  const { budgetPermission = BudgetPermissions.WRITE } = props
  const action = useRef<BudgetActions>(BudgetActions.NONE)
  const originalValue = useRef<BudgetWithIds | undefined>()
  const [permission, setPermission] = useState(budgetPermission)
  const { save, update, remove } = useBudgetMutation()

  const setAction = useCallback((newAction: BudgetActions) => {
    action.current = newAction
  }, [])

  const setOriginalValue = useCallback((value: BudgetWithIds) => {
    originalValue.current = value
  }, [])

  const setPermissionCallback = useCallback(
    (newPermission: BudgetPermissions) => {
      setPermission(newPermission)
    },
    []
  )

  const processBudget: ProcessBudgetType = useCallback(
    async (params) => {
      const { budget, promotionId, promotionName } = params

      try {
        switch (action.current) {
          case BudgetActions.CREATE: {
            if (originalValue.current?.id) {
              await remove(originalValue.current.id)
            }
            const response = await save({
              amount: (budget as BaseBudget).amount,
              promotionId,
              promotionName,
              notification: (budget as BaseBudget).notification,
            })
            return {
              success: true,
              budget: response,
            }
          }
          case BudgetActions.UPDATE: {
            const response = await update({
              ...(budget as BudgetWithIds),
              promotionId,
              promotionName,
            })
            return {
              success: true,
              budget: response,
            }
          }
          case BudgetActions.DELETE: {
            await remove((originalValue.current as BudgetWithIds).id)
            return {
              success: true,
            }
          }
          default:
            return {
              budget: budget as BudgetWithIds,
              success: true,
            }
        }
      } catch (error) {
        return {
          success: false,
          errorCode: error.status,
        }
      }
    },
    [remove, save, update]
  )

  return {
    action,
    originalValue,
    permission,
    setAction,
    setOriginalValue,
    setPermission: setPermissionCallback,
    processBudget,
  }
}

const [BudgetProvider, useBudgetContext, useBudgetProcessing] = constate(
  useBudget,
  (value): BudgetContextType => ({
    action: value.action,
    originalValue: value.originalValue,
    permission: value.permission,
    setOriginalValue: value.setOriginalValue,
    setPermission: value.setPermission,
    setAction: value.setAction,
  }),
  (value): BudgetProcessingType => ({
    processBudget: value.processBudget,
  })
)

export { BudgetProvider, useBudgetContext, useBudgetProcessing }
